export type StepState = 'läuft' | 'abgeschlossen' | 'fehlgeschlagen';

export interface StepRecord {
  name: string;
  startedAtMs: number;
  endedAtMs?: number;
  state: StepState;
}

export interface StartupTrace {
  now(): number;
  beginStep(name: string): void;
  endStep(name: string, state: Exclude<StepState, 'läuft'>): void;
  steps(): StepRecord[];
  lastStep(): StepRecord | undefined;
}

const defaultClock = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());

export function createStartupTrace(clock: () => number = defaultClock): StartupTrace {
  const records: StepRecord[] = [];

  return {
    now: clock,
    beginStep(name) {
      if (records.some((record) => record.name === name)) return;
      records.push({ name, startedAtMs: clock(), state: 'läuft' });
    },
    endStep(name, state) {
      const record = records.find((candidate) => candidate.name === name);
      if (!record || record.endedAtMs !== undefined) return;
      record.endedAtMs = clock();
      record.state = state;
    },
    steps: () => records.map((record) => ({ ...record })),
    lastStep: () => (records.length ? { ...records[records.length - 1] } : undefined),
  };
}

export const startupTrace = createStartupTrace();

export interface TrackedRequest {
  group: string;
  path: string;
  startedAtMs: number;
  endedAtMs?: number;
}

export interface RequestTracker {
  begin(group: string, path: string): void;
  end(path: string): void;
  all(): TrackedRequest[];
  pending(): TrackedRequest[];
}

export function createRequestTracker(clock: () => number = defaultClock): RequestTracker {
  const requests: TrackedRequest[] = [];

  return {
    begin(group, path) {
      requests.push({ group, path, startedAtMs: clock() });
    },
    end(path) {
      const request = requests.find((candidate) => candidate.path === path && candidate.endedAtMs === undefined);
      if (request) request.endedAtMs = clock();
    },
    all: () => requests.map((request) => ({ ...request })),
    pending: () => requests.filter((request) => request.endedAtMs === undefined).map((request) => ({ ...request })),
  };
}

export const startupRequests = createRequestTracker();

export interface ResourceEntryLike {
  name: string;
  startTime: number;
  fetchStart: number;
  connectStart: number;
  connectEnd: number;
  requestStart: number;
  responseStart: number;
  responseEnd: number;
  duration: number;
  nextHopProtocol: string;
  responseStatus?: number;
}

export interface SelectionOptions {
  rightsPaths: string[];
  idpOrigin?: string;
  mainScriptPath?: string;
}

export type EntryGroup = 'Rechte' | 'Anmeldung (IdP)' | 'Anwendungspaket';

export interface SelectedEntry {
  group: EntryGroup;
  entry: ResourceEntryLike;
}

export const RIGHTS_PATHS = ['/role/all', '/permission'];

const roundMs = (milliseconds: number): number => Math.round(milliseconds);

export function formatSeconds(milliseconds: number): string {
  return `${(milliseconds / 1000).toFixed(1).replace('.', ',')} s`;
}

export function toPath(url: string): string {
  try {
    return new URL(url, 'http://localhost').pathname;
  } catch {
    return url.split(/[?#]/)[0];
  }
}

function originOf(url: string): string {
  try {
    return new URL(url).origin;
  } catch {
    return '';
  }
}

export function readResourceEntries(
  source: Pick<Performance, 'getEntriesByType'> | undefined = typeof performance !== 'undefined'
    ? performance
    : undefined
): ResourceEntryLike[] | null {
  try {
    if (!source) return null;
    return Array.from(source.getEntriesByType('resource')) as unknown as ResourceEntryLike[];
  } catch {
    return null;
  }
}

export function selectStartupEntries(entries: ResourceEntryLike[], options: SelectionOptions): SelectedEntry[] {
  const selected: SelectedEntry[] = [];
  for (const entry of entries) {
    const path = toPath(entry.name);
    if (options.rightsPaths.some((rightsPath) => path.endsWith(rightsPath))) {
      selected.push({ group: 'Rechte', entry });
    } else if (options.idpOrigin && originOf(entry.name) === options.idpOrigin) {
      selected.push({ group: 'Anmeldung (IdP)', entry });
    } else if (options.mainScriptPath && path === options.mainScriptPath) {
      selected.push({ group: 'Anwendungspaket', entry });
    }
  }
  return selected;
}

const NO_TIMING = 'Aufteilung und Protokoll nicht verfügbar';

function describeStatus(status: number | undefined): string {
  if (status === undefined) return 'Status unbekannt';
  return status === 0 ? 'ohne Antwort' : `Status ${status}`;
}

function describeTiming(entry: ResourceEntryLike): string {
  if (!(entry.requestStart > 0 && entry.responseStart > 0)) return NO_TIMING;
  const waiting = roundMs(entry.requestStart - entry.fetchStart);
  const connecting = roundMs(entry.connectEnd - entry.connectStart);
  const responding = roundMs(entry.responseStart - entry.requestStart);
  const protocol = entry.nextHopProtocol ? `Protokoll ${entry.nextHopProtocol}` : 'Protokoll nicht verfügbar';
  return `Wartezeit ${waiting} ms, davon Verbindungsaufbau ${connecting} ms, Antwortzeit ${responding} ms, ${protocol}`;
}

export function describeEntry(group: EntryGroup, entry: ResourceEntryLike): string {
  return `  ${group} ${toPath(entry.name)}: ${describeStatus(entry.responseStatus)}, ${roundMs(entry.duration)} ms (${describeTiming(entry)})`;
}

export interface RequestLinesInput {
  nowMs: number;
  tracked: TrackedRequest[];
  entries: ResourceEntryLike[] | null;
  options: SelectionOptions;
}

export function buildRequestLines({ nowMs, tracked, entries, options }: RequestLinesInput): string[] {
  const lines: string[] = [];
  const browserDataMissing = !entries || entries.length === 0;
  if (browserDataMissing) lines.push('  Browserdaten: nicht verfügbar');

  const selected = selectStartupEntries(entries ?? [], options);
  const used = new Set<ResourceEntryLike>();

  for (const request of tracked) {
    if (request.endedAtMs === undefined) {
      lines.push(
        `  ${request.group} ${request.path}: ausstehend seit ${roundMs(nowMs - request.startedAtMs)} ms (${NO_TIMING})`
      );
      continue;
    }
    const match = selected.find(
      ({ group, entry }) => group === request.group && toPath(entry.name).endsWith(request.path) && !used.has(entry)
    );
    if (match) {
      used.add(match.entry);
      lines.push(describeEntry(match.group, match.entry));
    } else {
      const duration = roundMs(request.endedAtMs - request.startedAtMs);
      lines.push(
        `  ${request.group} ${request.path}: abgeschlossen nach ${duration} ms (kein Browser-Eintrag, ${NO_TIMING})`
      );
    }
  }

  const rest = selected.filter(({ entry }) => !used.has(entry)).sort((a, b) => a.entry.startTime - b.entry.startTime);
  for (const { group, entry } of rest) lines.push(describeEntry(group, entry));

  return lines;
}

export function attempt<T>(action: () => T): T | undefined;
export function attempt<T>(action: () => T, fallback: T): T;
export function attempt<T>(action: () => T, fallback?: T): T | undefined {
  try {
    return action();
  } catch {
    return fallback;
  }
}

export interface StartupReportInput {
  trace: StartupTrace;
  requests: RequestTracker;
  doc?: Document;
  idpUrl?: string;
  readEntries?: () => ResourceEntryLike[] | null;
}

const reportedTraces = new WeakSet<StartupTrace>();

export function writeDiagnosticsOnce(trace: StartupTrace, report: () => string, write: (text: string) => void): void {
  if (reportedTraces.has(trace)) return;
  write(report());
  reportedTraces.add(trace);
}

export function findMainScript(doc: Document | undefined): string | undefined {
  const sources = Array.from(doc?.querySelectorAll('script[type="module"][src]') ?? [], (script) =>
    script.getAttribute('src')
  );
  return sources.find((source): source is string => !!source && !toPath(source).startsWith('/@'));
}

export function buildStartupReport({
  trace,
  requests,
  doc,
  idpUrl,
  readEntries = () => readResourceEntries(),
}: StartupReportInput): string {
  const nowMs = trace.now();
  const mainScript = attempt(() => findMainScript(doc));
  const options: SelectionOptions = {
    rightsPaths: RIGHTS_PATHS,
    idpOrigin: idpUrl ? originOf(idpUrl) || undefined : undefined,
    mainScriptPath: mainScript ? toPath(mainScript) : undefined,
  };
  const requestLines = attempt(
    () => buildRequestLines({ nowMs, tracked: requests.all(), entries: readEntries(), options }),
    ['  Anfragen: nicht verfügbar']
  );
  const pendingCount = attempt(() => requests.pending().length, 0);
  return buildDiagnosticsText({ elapsedMs: nowMs, steps: trace.steps(), requestLines, pendingCount });
}

export interface StartupHookStep {
  name: string;
  state: StepState;
  durationMs: number;
}

export interface StartupDiagnosticsHook {
  lastStep(): StartupHookStep | null;
  report(): void;
}

declare global {
  interface Window {
    __startupDiagnostics?: StartupDiagnosticsHook;
  }
}

export interface StartupHookInput {
  trace: StartupTrace;
  requests: RequestTracker;
  write: (text: string) => void;
  doc?: Document;
  idpUrl?: string;
}

export function createStartupHook({ trace, requests, write, doc, idpUrl }: StartupHookInput): StartupDiagnosticsHook {
  return {
    lastStep() {
      return attempt<StartupHookStep | null>(() => {
        const step = trace.lastStep();
        if (!step) return null;
        return { name: step.name, state: step.state, durationMs: (step.endedAtMs ?? trace.now()) - step.startedAtMs };
      }, null);
    },
    report() {
      attempt(() => writeDiagnosticsOnce(trace, () => buildStartupReport({ trace, requests, doc, idpUrl }), write));
    },
  };
}

export function buildStepLine(step: StepRecord | undefined, nowMs: number): string {
  if (!step) return 'kein Startschritt erreicht';
  if (step.state === 'läuft') {
    return `Zuletzt erreicht: ${step.name} (läuft seit ${formatSeconds(nowMs - step.startedAtMs)})`;
  }
  return `Zuletzt erreicht: ${step.name} (${step.state} nach ${formatSeconds((step.endedAtMs ?? nowMs) - step.startedAtMs)})`;
}

function describeStep(step: StepRecord, nowMs: number): string {
  const duration = (step.endedAtMs ?? nowMs) - step.startedAtMs;
  return `  ${step.name}: ab ${roundMs(step.startedAtMs)} ms, ${roundMs(duration)} ms, ${step.state}`;
}

export interface DiagnosticsTextInput {
  elapsedMs: number;
  steps: StepRecord[];
  requestLines?: string[];
  pendingCount?: number;
}

export function buildDiagnosticsText({ elapsedMs, steps, requestLines, pendingCount }: DiagnosticsTextInput): string {
  const lines = [
    `[Start-Diagnose] Startfehler nach ${roundMs(elapsedMs)} ms`,
    'Schritte:',
    ...(steps.length ? steps.map((step) => describeStep(step, elapsedMs)) : ['  kein Startschritt erreicht']),
    buildStepLine(steps[steps.length - 1], elapsedMs),
  ];
  if (requestLines) {
    lines.push('Anfragen:', ...(requestLines.length ? requestLines : ['  keine erfasst']));
  }
  if (pendingCount !== undefined) {
    lines.push(`Ausstehende Anfragen: ${pendingCount} (erfasst werden nur die Rechte-Anfragen)`);
  }
  return lines.join('\n');
}
